/**
 * Listing Detail Page
 * Professional full-page view for marketplace listings
 *
 * API Response Structure:
 * - borrower: { name, phone, email, pan, has_full_pii }
 * - lead_details: { product_type, loan_amount_required, city, state, employment_type, cibil_score, etc. }
 * - quality: { score, grade, breakdown, grade_explanation }
 * - documents: { count, available: [...] }
 * - seller: { display_name, company_name, years_of_experience, total_deals_closed, etc. }
 * - collaboration: { commission_percentage, max_collaborators, current_collaborators, is_open }
 * - metrics: { view_count, interest_count }
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressBar } from 'primereact/progressbar';
import { Avatar } from 'primereact/avatar';
import { Panel } from 'primereact/panel';
import { Chip } from 'primereact/chip';
import { Badge } from 'primereact/badge';
import { LeadGradeBadge, ScopeBadge } from './components';
import {
    marketplaceService,
    formatLoanAmount,
    formatTimeAgo,
    getLoanProductConfig,
} from '../../services/marketplaceService';
import { ExpressInterestDialog } from '../../components/marketplace';
import DynamicLeadDetails from '../../components/marketplace/DynamicLeadDetails';
import MarketplaceDocumentsSection from '../../components/marketplace/MarketplaceDocumentsSection';
// Reuse the SAME offers component the lead wizard uses — read-only here —
// so the marketplace offer view stays consistent with View Leads and any
// change to the shared component reflects in both.
import OffersComparison from '../../components/Offers/OffersComparison';
// Reuse the SAME portfolio + CAM components the lead view uses — read-only
// here — so the marketplace view stays consistent and any design change to
// those components reflects here automatically (no fork).
import LoanPortfolioStep from '../../components/OfferCalculation/LoanPortfolioStep';
import CAMPaperView from '../../components/OfferCalculation/CAMPaperView';
import useMarketplaceDocuments from '../../hooks/useMarketplaceDocuments';
import { Tooltip } from 'primereact/tooltip';

// Outline style for Tags - border only, no solid fill
const outlineTagStyle = (severity) => {
    const colors = {
        warning: { background: 'transparent', border: '1.5px solid #EAB308', color: '#EAB308' },
        info: { background: 'transparent', border: '1.5px solid #3B82F6', color: '#3B82F6' },
        success: { background: 'transparent', border: '1.5px solid #22C55E', color: '#22C55E' },
        danger: { background: 'transparent', border: '1.5px solid #EF4444', color: '#EF4444' },
        secondary: { background: 'transparent', border: '1.5px solid #94A3B8', color: '#64748B' },
    };
    return colors[severity] || colors.secondary;
};

// Risk level → PrimeReact severity (portfolio header tag).
const riskSeverity = (level) => {
    const map = { low: 'success', medium: 'warning', high: 'danger' };
    return map[String(level || '').toLowerCase()] || 'info';
};

// Normalise a PII-safe offer (from the backend offers list) into the shape
// LoanPortfolioStep / CAMPaperView read — same alias mapping OffersStep uses.
const mapOfferForWidget = (o) => (o ? {
    ...o,
    bank_name: o.bank_name || o.lender_name,
    lender_name: o.lender_name || o.bank_name,
    emi: o.emi,
    EMI: o.emi,
    roi: o.roi || o.interest_rate,
    ROI: o.roi || o.interest_rate,
    interest_rate: o.interest_rate || o.roi,
    tenure: o.tenure || o.tenure_months,
    Tenure: o.tenure || o.tenure_months,
    tenure_months: o.tenure_months || o.tenure,
    loan_amount: o.loan_amount,
    LoanAmount: o.loan_amount,
    processing_fee: o.processing_fee,
    PF: o.processing_fee || 0,
} : {});

// Helper function to format expiry display professionally
const formatExpiryDisplay = (daysUntilExpiry, expiresAt, isExpired) => {
    if (isExpired) {
        return {
            primary: 'Expired',
            secondary: null,
            severity: 'expired',
            icon: 'pi-times-circle',
            bgClass: 'bg-gray-100',
            textClass: 'text-gray-500',
            iconClass: 'text-gray-400'
        };
    }

    const days = Math.ceil(Number(daysUntilExpiry) || 0);
    const expiryDate = expiresAt ? new Date(expiresAt) : null;
    const dateStr = expiryDate ? expiryDate.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short'
    }) : null;

    if (days <= 0) {
        return {
            primary: 'Today',
            secondary: 'Expires today!',
            severity: 'urgent',
            icon: 'pi-exclamation-circle',
            bgClass: 'bg-red-50',
            textClass: 'text-red-600',
            iconClass: 'text-red-500',
            pulse: true
        };
    } else if (days <= 3) {
        return {
            primary: `${days} day${days > 1 ? 's' : ''}`,
            secondary: 'Urgent',
            severity: 'urgent',
            icon: 'pi-exclamation-triangle',
            bgClass: 'bg-red-50',
            textClass: 'text-red-600',
            iconClass: 'text-red-500',
            pulse: true
        };
    } else if (days <= 7) {
        return {
            primary: `${days} days`,
            secondary: dateStr,
            severity: 'warning',
            icon: 'pi-clock',
            bgClass: 'bg-orange-50',
            textClass: 'text-orange-600',
            iconClass: 'text-orange-500'
        };
    } else if (days <= 30) {
        return {
            primary: `${days} days`,
            secondary: dateStr,
            severity: 'normal',
            icon: 'pi-clock',
            bgClass: 'bg-blue-50',
            textClass: 'text-blue-600',
            iconClass: 'text-blue-500'
        };
    } else {
        return {
            primary: dateStr || `${days} days`,
            secondary: null,
            severity: 'normal',
            icon: 'pi-calendar',
            bgClass: 'bg-blue-50',
            textClass: 'text-blue-600',
            iconClass: 'text-blue-500'
        };
    }
};

const ListingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [listing, setListing] = useState(null);
    const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
    const [purchaseNotes, setPurchaseNotes] = useState('');
    const [purchasing, setPurchasing] = useState(false);
    const [showInterestDialog, setShowInterestDialog] = useState(false);
    const [showCamDialog, setShowCamDialog] = useState(false);

    // Fetch listing details
    useEffect(() => {
        const fetchListing = async () => {
            setLoading(true);
            try {
                const response = await marketplaceService.getListingDetails(id);
                if (response.success) {
                    setListing(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch listing:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load listing details'
                });
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchListing();
    }, [id]);

    // Handle successful interest expression (callback from dialog)
    const handleInterestSuccess = async () => {
        setShowInterestDialog(false);
        toast.current?.show({
            severity: 'success',
            summary: 'Enquiry Submitted',
            detail: 'Your interest and commission proposal have been sent to the lead owner'
        });
        // Refresh listing
        try {
            const updated = await marketplaceService.getListingDetails(id);
            if (updated.success) setListing(updated.data);
        } catch (error) {
            console.error('Failed to refresh listing:', error);
        }
    };

    // Handle purchase
    const handlePurchase = async () => {
        setPurchasing(true);
        try {
            const response = await marketplaceService.purchaseLead(id, { notes: purchaseNotes });
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Purchase Successful!',
                    detail: 'Lead has been added to your purchases'
                });
                setShowPurchaseDialog(false);
                navigate('/app/marketplace/my-purchases');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Purchase Failed',
                detail: error.message || 'Failed to complete purchase'
            });
        } finally {
            setPurchasing(false);
        }
    };

    // Dynamic documents — same canonical resolver the lead wizard &
    // collaboration view use (blueprint §13.11). MUST be called before any
    // early return (hook rules). Read-only here: collaborationId=null →
    // upload/delete no-op; canUpload=false. Fed the PII-safe master-IDs
    // stub (document_master_data), never raw lead_data.
    const docResolver = useMarketplaceDocuments({
        leadData: listing?.document_master_data || null,
        collaborationId: null,
        documents: listing?.documents?.items || [],
        canUpload: false,
        toast,
    });

    // Loading skeleton
    if (loading) {
        return (
            <div className="grid">
                <div className="col-12 lg:col-8">
                    <Card className="shadow-1 mb-3">
                        <Skeleton height="2.5rem" width="60%" className="mb-3" />
                        <Skeleton height="1.2rem" width="40%" className="mb-4" />
                        <div className="surface-100 border-round p-4 mb-4">
                            <Skeleton height="150px" />
                        </div>
                        <Skeleton height="100px" />
                    </Card>
                </div>
                <div className="col-12 lg:col-4">
                    <Card className="shadow-1">
                        <Skeleton height="250px" />
                    </Card>
                </div>
            </div>
        );
    }

    // Not found
    if (!listing) {
        return (
            <div className="flex flex-column align-items-center justify-content-center py-8">
                <div className="bg-orange-50 border-circle p-5 mb-4">
                    <i className="pi pi-exclamation-triangle text-orange-500 text-5xl" />
                </div>
                <h3 className="text-900 font-semibold mb-2">Listing Not Found</h3>
                <p className="text-600 mb-4 text-center" style={{ maxWidth: '400px' }}>
                    This listing may have been removed, expired, or you don't have access to view it.
                </p>
                <Button
                    label="Back to Marketplace"
                    icon="pi pi-arrow-left"
                    onClick={() => navigate('/app/marketplace')}
                />
            </div>
        );
    }

    // Extract data from API response
    const {
        tracking_code,
        borrower = {},
        lead_details = {},
        lead_body: leadBody = null,            // canonical schema-driven body (blueprint §5.4)
        document_master_data: documentMasterData = null, // PII-safe master IDs for the doc resolver
        snapshot = null,                       // worked-ness snapshot (Phase 2)
        offers = [],                           // PII-safe generated offers (collapsible section)
        cam = null,                            // Credit Appraisal Memo (identity masked on the fly)
        portfolio = null,                      // loan portfolio (identity masked on the fly)
        quality = {},
        documents = {},
        seller: sellerData,
        posted_by: postedByData,
        collaboration = {},
        metrics = {},
        marketplace_scope,
        posted_at,
        expires_at,
        days_until_expiry,
        is_expired,
        status,
        updated_since_interest,
        is_owner,
        is_member, // User is already a collaborator on this lead
        my_collaboration_id, // Collaboration ID for direct navigation
        has_expressed_interest,
        has_purchased
    } = listing;

    // Copy tracking code to clipboard
    const copyTrackingCode = () => {
        if (tracking_code) {
            navigator.clipboard.writeText(tracking_code);
            toast.current?.show({
                severity: 'success',
                summary: 'Copied',
                detail: `Tracking code ${tracking_code} copied to clipboard`,
                life: 2000
            });
        }
    };

    const seller = sellerData || postedByData || {};
    // Use product_type for config lookup, fallback to product_type_detailed for label
    const productConfig = getLoanProductConfig(lead_details.product_type);
    // If product_type didn't match, use product_type_detailed as display label
    const productLabel = productConfig.label !== 'Unknown'
        ? productConfig.label
        : (lead_details.product_type_detailed || lead_details.product_type || 'Unknown');
    const slotsAvailable = (collaboration.max_collaborators || 1) - (collaboration.current_collaborators || 0);

    // Get organization/business info based on employment type
    const getOrganizationInfo = () => {
        const empType = lead_details.employment_type;
        if (empType === 'salaried') {
            return { label: 'Company', value: lead_details.company_name, icon: 'pi-building' };
        } else if (empType === 'self_employed') {
            return { label: 'Business Type', value: lead_details.business_type, icon: 'pi-briefcase' };
        } else if (empType === 'self_employed_professional') {
            return { label: 'Profession', value: lead_details.profession_type, icon: 'pi-user' };
        }
        // Fallback - check all fields
        if (lead_details.company_name) {
            return { label: 'Company', value: lead_details.company_name, icon: 'pi-building' };
        }
        if (lead_details.business_type) {
            return { label: 'Business Type', value: lead_details.business_type, icon: 'pi-briefcase' };
        }
        if (lead_details.profession_type) {
            return { label: 'Profession', value: lead_details.profession_type, icon: 'pi-user' };
        }
        return null;
    };

    const organizationInfo = getOrganizationInfo();

    const getGradeDescription = (grade) => {
        const descriptions = {
            'A': 'Premium quality lead with verified documents and excellent credentials',
            'B': 'High quality lead with good credentials and documentation',
            'C': 'Standard lead with basic verification completed',
            'D': 'Lead requires additional verification steps',
            'E': 'Basic lead information available for review'
        };
        return descriptions[grade] || 'Lead quality being assessed';
    };

    const getGradeColor = (grade) => {
        const colors = { 'A': 'green', 'B': 'blue', 'C': 'orange', 'D': 'yellow', 'E': 'red' };
        return colors[grade] || 'gray';
    };

    return (
        <div className="listing-detail">
            <Toast ref={toast} />

            {/* Breadcrumb Header - Mobile Optimized */}
            <div className="surface-card border-round shadow-1 p-1 md:p-2 mb-2 md:mb-3">
                <div className="flex align-items-center justify-content-between gap-1">
                    <div className="flex align-items-center gap-1 min-w-0">
                        <Button
                            icon="pi pi-arrow-left"
                            className="p-button-text p-button-rounded p-button-sm flex-shrink-0"
                            onClick={() => navigate('/app/marketplace')}
                            tooltip="Back to Marketplace"
                            tooltipOptions={{ position: 'top' }}
                            style={{ width: '1.75rem', height: '1.75rem' }}
                        />
                        <span className="text-500 hidden md:inline" style={{ fontSize: '0.75rem' }}>Marketplace</span>
                        <i className="pi pi-angle-right text-400 hidden md:inline" style={{ fontSize: '0.65rem' }} />
                        <span className="text-900 font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.75rem' }}>
                            Lead Details
                        </span>
                    </div>
                    {tracking_code && (
                        <div
                            className="flex align-items-center gap-1 bg-primary-50 border-round cursor-pointer flex-shrink-0"
                            onClick={copyTrackingCode}
                            title="Click to copy"
                            style={{ padding: '0.2rem 0.5rem' }}
                        >
                            <i className="pi pi-hashtag text-primary" style={{ fontSize: '0.65rem' }} />
                            <span className="font-mono text-primary font-semibold" style={{ fontSize: '0.7rem' }}>
                                {tracking_code}
                            </span>
                            <i className="pi pi-copy text-primary hidden md:inline" style={{ fontSize: '0.6rem' }} />
                        </div>
                    )}
                </div>
            </div>

            <div className="grid">
                {/* Main Content */}
                <div className="col-12 lg:col-8">
                    {/* Lead Overview Card */}
                    <Card className="shadow-1 mb-2">
                        {/* Header */}
                        <div className="flex flex-column md:flex-row md:align-items-start md:justify-content-between gap-1 md:gap-2 mb-2 pb-2 border-bottom-1 border-200">
                            <div className="flex-1 min-w-0">
                                {/* Tags row */}
                                <div className="flex align-items-center gap-1 mb-1 flex-wrap">
                                    <Tag
                                        value={productLabel}
                                        icon={`pi ${productConfig.icon}`}
                                        style={{ ...outlineTagStyle('info'), fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}
                                    />
                                    <ScopeBadge scope={marketplace_scope} />
                                    {is_expired && <Tag value="Expired" style={{ ...outlineTagStyle('danger'), fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />}
                                    {status === 'in_progress' && <Tag value="In Progress" style={{ ...outlineTagStyle('warning'), fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />}
                                    {updated_since_interest && <Tag value="Updated" icon="pi pi-sync" style={{ ...outlineTagStyle('success'), fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />}
                                </div>
                                {/* Name + Grade on mobile in single row */}
                                <div className="flex align-items-center justify-content-between gap-1 mb-1">
                                    <h2 className="text-900 font-bold m-0 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '1rem' }}>
                                        {borrower.full_name || borrower.name || 'Lead Details'}
                                    </h2>
                                    <div className="flex-shrink-0 md:hidden">
                                        <LeadGradeBadge grade={quality.grade} size="small" />
                                    </div>
                                </div>
                                {/* Tracking code - hidden on mobile since it's in header */}
                                {tracking_code && (
                                    <div className="hidden md:flex align-items-center gap-1 mb-1">
                                        <Tooltip target=".tracking-code-chip" position="top" />
                                        <Chip
                                            label={tracking_code}
                                            icon="pi pi-hashtag"
                                            className="tracking-code-chip bg-gray-100 text-700 font-mono cursor-pointer"
                                            onClick={copyTrackingCode}
                                            data-pr-tooltip="Click to copy tracking code"
                                            style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
                                        />
                                    </div>
                                )}
                                {/* Loan amount */}
                                <div className="text-green-600 font-bold" style={{ fontSize: '1.25rem' }}>
                                    {formatLoanAmount(lead_details.loan_amount_required)}
                                </div>
                            </div>
                            {/* Grade badge - desktop only */}
                            <div className="hidden md:flex align-items-center gap-2 flex-shrink-0" style={{ transform: 'scale(0.85)' }}>
                                <LeadGradeBadge grade={quality.grade} size="large" showLabel />
                            </div>
                        </div>

                        {/* Quick Stats */}
                        {(() => {
                            const expiryInfo = formatExpiryDisplay(days_until_expiry, expires_at, is_expired);
                            const fullExpiryDate = expires_at ? new Date(expires_at).toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            }) : 'Not specified';

                            return (
                                <div className="grid mb-2 quick-stats-grid">
                                    <div className="col-6 md:col-3 p-1">
                                        <div className="text-center surface-100 border-round h-full stat-card" style={{ padding: '0.5rem' }}>
                                            <i className="pi pi-eye text-primary stat-icon mb-1" style={{ display: 'block' }} />
                                            <div className="text-900 font-bold stat-value">{metrics.view_count || 0}</div>
                                            <div className="text-500 stat-label">Views</div>
                                        </div>
                                    </div>
                                    <div className="col-6 md:col-3 p-1">
                                        <div className="text-center surface-100 border-round h-full stat-card" style={{ padding: '0.5rem' }}>
                                            <i className="pi pi-heart text-pink-500 stat-icon mb-1" style={{ display: 'block' }} />
                                            <div className="text-900 font-bold stat-value">{metrics.interest_count || 0}</div>
                                            <div className="text-500 stat-label">Enquiries</div>
                                        </div>
                                    </div>
                                    <div className="col-6 md:col-3 p-1">
                                        <div className="text-center surface-100 border-round h-full stat-card" style={{ padding: '0.5rem' }}>
                                            <i className="pi pi-users text-green-500 stat-icon mb-1" style={{ display: 'block' }} />
                                            <div className="text-900 font-bold stat-value">{slotsAvailable}/{collaboration.max_collaborators || 1}</div>
                                            <div className="text-500 stat-label">Spots</div>
                                        </div>
                                    </div>
                                    <div className="col-6 md:col-3 p-1">
                                        <Tooltip target=".expiry-stat-card" position="top" />
                                        <div
                                            className={`expiry-stat-card text-center border-round h-full stat-card ${expiryInfo.bgClass}`}
                                            data-pr-tooltip={`Expires on ${fullExpiryDate}`}
                                            style={{ padding: '0.5rem' }}
                                        >
                                            <i className={`pi ${expiryInfo.icon} ${expiryInfo.iconClass} stat-icon mb-1 ${expiryInfo.pulse ? 'expiry-pulse' : ''}`} style={{ display: 'block' }} />
                                            <div className={`font-bold stat-value ${expiryInfo.textClass}`}>
                                                {expiryInfo.primary}
                                            </div>
                                            <div className={`stat-label ${expiryInfo.severity === 'urgent' ? expiryInfo.textClass : 'text-500'}`}>
                                                {expiryInfo.secondary || 'Expires'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Lead Location (confirmed at posting — blueprint
                            §13.10b). The canonical market this lead is for;
                            buyers filter on it. */}
                        {(lead_details.city || lead_details.state) && (
                            <div className="flex align-items-center gap-2 surface-50 border-round mb-2" style={{ padding: '0.5rem 0.75rem' }}>
                                <i className="pi pi-map-marker text-primary" style={{ fontSize: '0.85rem' }} />
                                <span className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>
                                    {[lead_details.city, lead_details.state].filter(Boolean).join(', ')}
                                </span>
                                <span className="text-500" style={{ fontSize: '0.65rem' }}>Lead location</span>
                            </div>
                        )}

                        {/* Borrower Information */}
                        <div className="mb-2">
                            <div className="flex align-items-center gap-1 mb-1">
                                <i className="pi pi-user text-primary" style={{ fontSize: '0.75rem' }} />
                                <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Borrower Information</h4>
                                <Tag value="Protected" icon="pi pi-lock" className="ml-auto" style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '0.1rem 0.25rem' }} />
                            </div>
                            <div className="surface-50 border-round" style={{ padding: '0.5rem' }}>
                                <div className="grid">
                                    <div className="col-6 md:col-6">
                                        <div className="flex align-items-center gap-2 mb-2">
                                            <div className="flex align-items-center justify-content-center bg-primary-100 border-circle flex-shrink-0" style={{ width: '24px', height: '24px' }}>
                                                <i className="pi pi-user text-primary" style={{ fontSize: '0.65rem' }} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-500 mb-0" style={{ fontSize: '0.55rem' }}>Full Name</div>
                                                <div className="text-900 font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.7rem' }}>{borrower.full_name || borrower.name || 'N/A'}</div>
                                            </div>
                                        </div>
                                        <div className="flex align-items-center gap-2">
                                            <div className="flex align-items-center justify-content-center bg-green-100 border-circle flex-shrink-0" style={{ width: '24px', height: '24px' }}>
                                                <i className="pi pi-phone text-green-600" style={{ fontSize: '0.65rem' }} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-500 mb-0" style={{ fontSize: '0.55rem' }}>Phone</div>
                                                <div className="text-900 font-medium" style={{ fontSize: '0.7rem' }}>{borrower.phone || 'N/A'}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6 md:col-6">
                                        <div className="flex align-items-center gap-2 mb-2">
                                            <div className="flex align-items-center justify-content-center bg-blue-100 border-circle flex-shrink-0" style={{ width: '24px', height: '24px' }}>
                                                <i className="pi pi-envelope text-blue-600" style={{ fontSize: '0.65rem' }} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-500 mb-0" style={{ fontSize: '0.55rem' }}>Email</div>
                                                <div className="text-900 font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.7rem' }}>{borrower.email || 'N/A'}</div>
                                            </div>
                                        </div>
                                        <div className="flex align-items-center gap-2">
                                            <div className="flex align-items-center justify-content-center bg-purple-100 border-circle flex-shrink-0" style={{ width: '24px', height: '24px' }}>
                                                <i className="pi pi-id-card text-purple-600" style={{ fontSize: '0.65rem' }} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-500 mb-0" style={{ fontSize: '0.55rem' }}>PAN</div>
                                                <div className="text-900 font-medium" style={{ fontSize: '0.7rem' }}>{borrower.pan || 'N/A'}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-blue-50 border-round mt-2" style={{ padding: '0.35rem 0.5rem' }}>
                                    <p className="text-blue-700 m-0" style={{ fontSize: '0.65rem' }}>
                                        <i className="pi pi-info-circle mr-1" />
                                        Complete details revealed after purchase
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Lead details — schema-driven PRIMARY renderer
                            (blueprint §5.4/§13). Replaces the legacy
                            column-based "Lead Summary" grid which showed N/A
                            for any field that lives only in lead_data. Reads
                            the canonical `lead_body` (sections + source). */}
                        <div>
                            <div className="flex align-items-center gap-1 mb-2">
                                <i className="pi pi-list text-primary" style={{ fontSize: '0.75rem' }} />
                                <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>
                                    {leadBody?.loan_type ? `${leadBody.loan_type} Details` : 'Lead Details'}
                                </h4>
                            </div>
                            {leadBody?.sections?.length > 0 ? (
                                <DynamicLeadDetails
                                    sections={leadBody.sections}
                                    loanType={leadBody.loan_type}
                                    loanProduct={leadBody.loan_product}
                                />
                            ) : (
                                <div
                                    className="surface-50 border-round flex flex-column align-items-center"
                                    style={{ padding: '1.25rem' }}
                                >
                                    <i className="pi pi-info-circle text-400 mb-2" style={{ fontSize: '1.25rem' }} />
                                    <p className="text-600 m-0 text-center" style={{ fontSize: '0.75rem' }}>
                                        Detailed lead information is not available for this listing yet.
                                    </p>
                                </div>
                            )}
                        </div>
                    </Card>

                </div>

                {/* Sidebar */}
                <div className="col-12 lg:col-4">
                    {/* Seller Profile Card */}
                    <Card className="shadow-1 mb-2">
                        <div className="flex align-items-center gap-1 mb-2">
                            <i className="pi pi-user text-primary" style={{ fontSize: '0.75rem' }} />
                            <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Listed By</h4>
                        </div>

                        <div className="flex align-items-center gap-2 mb-2">
                            <Avatar
                                image={seller.profile_photo_url_full || seller.profile_photo_url}
                                icon="pi pi-user"
                                size="large"
                                shape="circle"
                                className="bg-primary-100 text-primary"
                                style={{ width: '2.5rem', height: '2.5rem' }}
                            />
                            <div className="flex-1">
                                <div className="flex align-items-center gap-1">
                                    <span className="text-900 font-bold" style={{ fontSize: '0.75rem' }}>{seller.full_name || seller.display_name || 'Agent'}</span>
                                    {seller.is_verified && (
                                        <i className="pi pi-verified text-primary" title="Verified Agent" style={{ fontSize: '0.7rem' }} />
                                    )}
                                </div>
                                <div className="text-500" style={{ fontSize: '0.65rem' }}>{seller.current_company || seller.company_name || 'Financial Services'}</div>
                            </div>
                        </div>

                        {seller.specializations?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                                {seller.specializations.slice(0, 4).map((spec, idx) => (
                                    <Tag key={idx} value={spec} style={{ ...outlineTagStyle('info'), fontSize: '0.55rem', padding: '0.1rem 0.25rem' }} />
                                ))}
                            </div>
                        )}

                        <div className="surface-50 border-round" style={{ padding: '0.5rem' }}>
                            <div className="flex justify-content-between mb-1">
                                <span className="text-500" style={{ fontSize: '0.65rem' }}>Experience</span>
                                <span className="text-900 font-medium" style={{ fontSize: '0.7rem' }}>{seller.experience_years || seller.years_of_experience || 0} years</span>
                            </div>
                            <div className="flex justify-content-between mb-1">
                                <span className="text-500" style={{ fontSize: '0.65rem' }}>Leads Processed</span>
                                <span className="text-900 font-medium" style={{ fontSize: '0.7rem' }}>{seller.total_leads_processed || seller.total_deals_closed || 0}</span>
                            </div>
                            {(seller.rating > 0 || seller.reputation_score > 0) && (
                                <div className="flex justify-content-between">
                                    <span className="text-500" style={{ fontSize: '0.65rem' }}>Rating</span>
                                    <span className="text-900 font-medium" style={{ fontSize: '0.7rem' }}>{seller.rating || seller.reputation_score}/5</span>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Collaboration Terms Card */}
                    <Card className="shadow-1 mb-2">
                        <div className="flex align-items-center gap-1 mb-2">
                            <i className="pi pi-handshake text-primary" style={{ fontSize: '0.75rem' }} />
                            <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Lead Deal Terms</h4>
                        </div>

                        <div className="text-center bg-primary-50 border-round" style={{ padding: '0.6rem' }}>
                            <div className="text-primary font-bold" style={{ fontSize: '1.75rem' }}>{parseFloat((Number(collaboration.commission_percentage) || 3.0).toFixed(3))}%</div>
                            <div className="text-primary-700 font-medium" style={{ fontSize: '0.7rem' }}>Lead Commission Requested</div>
                            <div className="text-500 mt-1" style={{ fontSize: '0.55rem' }}>from actual disbursed amount</div>
                        </div>

                        <div className="flex justify-content-between align-items-center surface-50 border-round my-2" style={{ padding: '0.5rem' }}>
                            <div>
                                <div className="text-500" style={{ fontSize: '0.6rem' }}>Available Spots</div>
                                <div className="text-900 font-bold" style={{ fontSize: '0.85rem' }}>{slotsAvailable} of {collaboration.max_collaborators || 1}</div>
                            </div>
                            <Tag
                                value={collaboration.is_open ? 'Open' : 'Full'}
                                icon={collaboration.is_open ? 'pi pi-check' : 'pi pi-times'}
                                style={{ ...outlineTagStyle(collaboration.is_open ? 'success' : 'danger'), fontSize: '0.55rem', padding: '0.15rem 0.3rem' }}
                            />
                        </div>

                        <ProgressBar
                            value={((collaboration.current_collaborators || 0) / (collaboration.max_collaborators || 1)) * 100}
                            showValue={false}
                            style={{ height: '0.3rem' }}
                         aria-label="Progress" />
                    </Card>

                    {/* Action Card */}
                    <Card className="shadow-1 sticky" style={{ top: '0.5rem' }}>
                        {is_owner ? (
                            <div className="flex flex-column gap-1">
                                <div className="text-center bg-blue-50 border-round mb-1" style={{ padding: '0.5rem' }}>
                                    <i className="pi pi-info-circle text-blue-600 mr-1" style={{ fontSize: '0.7rem' }} />
                                    <span className="text-blue-700" style={{ fontSize: '0.7rem' }}>This is your listing</span>
                                </div>
                                <Button
                                    label="Manage Listing"
                                    icon="pi pi-cog"
                                    className="w-full p-button-sm"
                                    onClick={() => navigate('/app/marketplace/my-listings')}
                                    style={{ fontSize: '0.75rem' }}
                                />
                            </div>
                        ) : has_purchased ? (
                            <div className="flex flex-column gap-1">
                                <div className="text-center bg-green-50 border-round mb-1" style={{ padding: '0.5rem' }}>
                                    <i className="pi pi-check-circle text-green-600 mb-1" style={{ display: 'block', fontSize: '1.25rem' }} />
                                    <div className="text-green-700 font-semibold" style={{ fontSize: '0.75rem' }}>Already Purchased</div>
                                </div>
                                <Button
                                    label="View in My Purchases"
                                    icon="pi pi-external-link"
                                    className="w-full p-button-success p-button-sm"
                                    onClick={() => navigate('/app/marketplace/my-purchases')}
                                    style={{ fontSize: '0.75rem' }}
                                />
                            </div>
                        ) : is_member ? (
                            /* User is already a collaborator on this lead */
                            <div className="flex flex-column gap-2">
                                <div className="text-center bg-green-50 border-round" style={{ padding: '0.5rem' }}>
                                    <i className="pi pi-check-circle text-green-500 mb-1" style={{ display: 'block', fontSize: '1.5rem' }} />
                                    <div className="text-900 font-semibold mb-0" style={{ fontSize: '0.75rem' }}>You're in a Lead Deal</div>
                                    <div className="text-500" style={{ fontSize: '0.65rem' }}>You are already working on this lead</div>
                                </div>
                                <Button
                                    label="View Lead Deal Details"
                                    icon="pi pi-arrow-right"
                                    className="w-full p-button-success p-button-sm"
                                    onClick={() => navigate(`/app/collaborations/${my_collaboration_id}`)}
                                    style={{ fontSize: '0.75rem' }}
                                />
                            </div>
                        ) : has_expressed_interest ? (
                            /* User has already expressed interest - pending response */
                            <div className="flex flex-column gap-2">
                                <div className="text-center bg-blue-50 border-round" style={{ padding: '0.5rem' }}>
                                    <i className="pi pi-clock text-blue-500 mb-1" style={{ display: 'block', animation: 'pulse 2s infinite', fontSize: '1.5rem' }} />
                                    <div className="text-900 font-semibold mb-0" style={{ fontSize: '0.75rem' }}>Enquiry Submitted</div>
                                    <div className="text-500" style={{ fontSize: '0.65rem' }}>Waiting for the lead owner's response</div>
                                </div>
                                <Button
                                    label="View My Enquiries"
                                    icon="pi pi-arrow-right"
                                    className="w-full p-button-outlined p-button-sm"
                                    onClick={() => navigate('/app/marketplace/my-interests')}
                                    style={{ fontSize: '0.75rem' }}
                                />
                                <div className="text-center text-500" style={{ fontSize: '0.6rem' }}>
                                    <i className="pi pi-info-circle mr-1" />
                                    You'll be notified when there's a response
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-column gap-2">
                                <Button
                                    label="Purchase This Lead"
                                    icon="pi pi-shopping-cart"
                                    className="w-full"
                                    onClick={() => setShowPurchaseDialog(true)}
                                    disabled={true}
                                    tooltip="Purchase option is currently disabled"
                                    tooltipOptions={{ position: 'top' }}
                                    style={{ fontSize: '0.75rem' }}
                                />
                                <Button
                                    label="Send Enquiry"
                                    icon="pi pi-handshake"
                                    className="w-full p-button-outlined p-button-sm"
                                    onClick={() => setShowInterestDialog(true)}
                                    disabled={is_expired || !collaboration.is_open}
                                    style={{ fontSize: '0.75rem' }}
                                />
                                {(is_expired || !collaboration.is_open) && (
                                    <div className="text-center text-500" style={{ fontSize: '0.6rem' }}>
                                        <i className="pi pi-info-circle mr-1" />
                                        {is_expired ? 'This listing has expired' : 'All collaboration spots are filled'}
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Full-width sections — offers onward. Wide content
                    (offer comparison, strength, documents) gets the full
                    page; top zone stays overview + action sidebar. */}
                <div className="col-12">
                    {/* Offers Generated — collapsible, collapsed by default.
                        PII-safe (bank / rate / EMI / tenure only). Sorted
                        best-first (selected, then lowest ROI). */}
                    {Array.isArray(offers) && offers.length > 0 && (
                        <Panel
                            toggleable
                            collapsed
                            className="shadow-1 mb-2"
                            header={
                                <span className="flex align-items-center gap-2">
                                    <i className="pi pi-tag text-primary" style={{ fontSize: '0.8rem' }} />
                                    <span className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>Offers Generated</span>
                                    <Tag value={offers.length} severity="info" className="text-xs" />
                                </span>
                            }
                        >
                            {/* Same component as the lead wizard / View Leads,
                                in read-only mode (no select / edit / share),
                                eligibility hidden (buyer pre-purchase). Offers
                                normalised to the same shape OffersStep feeds it.
                                scrollContained: offers scroll INSIDE a bounded box
                                (own scrollbar), the bank-name header pins to the box
                                top, and page scroll resumes at the box end — so the
                                offers don't spill down the whole detail page. */}
                            <OffersComparison
                                offers={offers.map((o) => ({
                                    ...o,
                                    bank_name: o.bank_name || o.lender_name,
                                    lenderName: o.bank_name || o.lender_name,
                                    EMI: o.emi,
                                    emi: o.emi,
                                    ROI: o.roi || o.interest_rate,
                                    roi: o.roi || o.interest_rate,
                                    effective_roi: o.effective_roi || o.roi || o.interest_rate,
                                    Tenure: o.tenure || o.tenure_months,
                                    tenure: o.tenure || o.tenure_months,
                                    LoanAmount: o.loan_amount,
                                    loan_amount: o.loan_amount,
                                    PF: o.processing_fee || 0,
                                    processing_fee: o.processing_fee,
                                }))}
                                readOnly
                                hideEligibility
                                scrollContained
                                scrollHeight="70vh"
                            />
                        </Panel>
                    )}

                    {/* Loan Portfolio — collapsible, collapsed by default.
                        Reuses the SAME read-only LoanPortfolioStep the lead
                        view (PortfolioTab) uses, fed from the masked snapshot.
                        `lead` is passed WITHOUT an id so the component renders
                        purely from props (no tenant portfolio API call). */}
                    {portfolio && (
                        <Panel
                            toggleable
                            collapsed
                            className="shadow-1 mb-2"
                            header={
                                <span className="flex align-items-center gap-2 flex-wrap">
                                    <i className="pi pi-chart-pie text-primary" style={{ fontSize: '0.8rem' }} />
                                    <span className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>Loan Portfolio</span>
                                    {portfolio.portfolio?.risk_level && (
                                        <Tag
                                            value={`Risk: ${portfolio.portfolio.risk_level}`}
                                            severity={riskSeverity(portfolio.portfolio.risk_level)}
                                            className="text-xs"
                                        />
                                    )}
                                    {portfolio.has_full_access === false && (
                                        <Tag value="Personal info hidden" icon="pi pi-lock" style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '0.1rem 0.3rem' }} />
                                    )}
                                </span>
                            }
                        >
                            <LoanPortfolioStep
                                lead={{ lead_data: {} }}
                                existingLoans={portfolio.existing_loans || []}
                                selectedOffer={mapOfferForWidget(portfolio.selected_offer)}
                                eligibilityResult={portfolio.eligibility || {}}
                                formData={portfolio.form_data || {}}
                                coApplicants={portfolio.co_applicants || []}
                                toast={toast}
                                readOnly
                                isLoading={false}
                            />
                        </Panel>
                    )}

                    {/* Credit Appraisal Memo — collapsible. CAMPaperView is a
                        full-screen Dialog (the lead view's renderer), so the
                        section is a compact summary + a button that opens that
                        SAME dialog. Identity masked on the fly by the backend. */}
                    {cam && (
                        <Panel
                            toggleable
                            collapsed
                            className="shadow-1 mb-2"
                            header={
                                <span className="flex align-items-center gap-2 flex-wrap">
                                    <i className="pi pi-file-edit text-primary" style={{ fontSize: '0.8rem' }} />
                                    <span className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>Credit Appraisal Memo</span>
                                    {cam.has_full_access === false && (
                                        <Tag value="Personal info hidden" icon="pi pi-lock" style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '0.1rem 0.3rem' }} />
                                    )}
                                </span>
                            }
                        >
                            <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2 surface-50 border-round" style={{ padding: '0.75rem' }}>
                                <div className="flex flex-column gap-1">
                                    <span className="text-500" style={{ fontSize: '0.6rem' }}>Loan Requested</span>
                                    <span className="text-900 font-bold" style={{ fontSize: '0.9rem' }}>{formatLoanAmount(lead_details.loan_amount_required)}</span>
                                </div>
                                {cam.eligibility?.eligible_amount != null && (
                                    <div className="flex flex-column gap-1">
                                        <span className="text-500" style={{ fontSize: '0.6rem' }}>Eligible Amount</span>
                                        <span className="text-900 font-bold" style={{ fontSize: '0.9rem' }}>{formatLoanAmount(cam.eligibility.eligible_amount)}</span>
                                    </div>
                                )}
                                <Button
                                    label="View Full Appraisal Memo"
                                    icon="pi pi-external-link"
                                    className="p-button-sm"
                                    onClick={() => setShowCamDialog(true)}
                                    style={{ fontSize: '0.75rem' }}
                                />
                            </div>
                        </Panel>
                    )}

                    {/* Lead Strength — worked-ness snapshot (Phase 2):
                        eligibility / offers / portfolio / existing loans /
                        verification / bureau. The decision-critical
                        financial-fitness signals a buyer evaluates
                        pre-purchase. PII-safe summary only. */}
                    {snapshot && (() => {
                        const e = snapshot.eligibility;
                        const o = snapshot.offers;
                        const p = snapshot.portfolio;
                        const el = snapshot.existing_loans;
                        const v = snapshot.verification || {};
                        const b = snapshot.bureau || {};
                        const vKeys = Object.keys(v);
                        const hasAny = e || o || p || (el && el.count > 0) || vKeys.length > 0 || b.has_cibil;

                        const Tile = ({ label, value, color = 'primary' }) => (
                            <div className="col-6 md:col-4 p-1">
                                <div className="surface-50 border-round h-full" style={{ padding: '0.4rem 0.5rem' }}>
                                    <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>{label}</div>
                                    <div className={`text-900 font-semibold text-${color}-700 white-space-nowrap overflow-hidden text-overflow-ellipsis`} style={{ fontSize: '0.72rem' }}>
                                        {value}
                                    </div>
                                </div>
                            </div>
                        );

                        return (
                            <Card className="shadow-1 mb-2">
                                <div className="flex align-items-center gap-1 mb-2">
                                    <i className="pi pi-chart-line text-primary" style={{ fontSize: '0.75rem' }} />
                                    <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Lead Strength</h4>
                                </div>

                                {!hasAny && (
                                    <div className="surface-50 border-round flex flex-column align-items-center" style={{ padding: '1rem' }}>
                                        <i className="pi pi-info-circle text-400 mb-2" style={{ fontSize: '1.1rem' }} />
                                        <p className="text-600 m-0 text-center" style={{ fontSize: '0.72rem' }}>
                                            This lead has not been fully assessed yet (no eligibility, offers or bureau pull).
                                        </p>
                                    </div>
                                )}

                                {e && (
                                    <div className="mb-2">
                                        <div className="text-700 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Eligibility</div>
                                        <div className="grid">
                                            {/* FOIR and EMI are shown ONCE below (Affordability /
                                                Offers) — for PL leads eligibility/offers/portfolio
                                                share the same offer-engine numbers, so repeating
                                                them here is noise. */}
                                            <Tile label="Eligible" value={e.is_eligible ? `Yes${e.eligibility_status ? ` (${e.eligibility_status})` : ''}` : (e.eligibility_status || 'No')} color={e.is_eligible ? 'green' : 'red'} />
                                            {e.eligible_amount != null && <Tile label="Eligible Amount" value={formatLoanAmount(e.eligible_amount)} color="green" />}
                                            {e.interest_rate != null && <Tile label="Interest" value={`${e.interest_rate}%`} color="blue" />}
                                            {e.tenure_months != null && <Tile label="Tenure" value={`${e.tenure_months} mo`} color="cyan" />}
                                        </div>
                                    </div>
                                )}

                                {o && (
                                    <div className="mb-2">
                                        <div className="text-700 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Offers</div>
                                        <div className="grid">
                                            <Tile label="Offers Generated" value={o.offers_count ?? 0} color="primary" />
                                            {o.best_offer_emi != null && <Tile label="Best EMI" value={formatLoanAmount(o.best_offer_emi)} color="green" />}
                                            <Tile label="Offer Selected" value={o.has_selected_offer ? 'Yes' : 'No'} color={o.has_selected_offer ? 'green' : 'orange'} />
                                        </div>
                                    </div>
                                )}

                                {p && (
                                    <div className="mb-2">
                                        <div className="text-700 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Affordability</div>
                                        <div className="grid">
                                            {p.foir_after != null && <Tile label="FOIR (after)" value={`${p.foir_after}%`} color="orange" />}
                                            {/* Combined EMI == new-loan EMI when there's no
                                                existing debt → identical to "Best EMI" above.
                                                Only show it when it genuinely differs (existing
                                                loans present), otherwise it's a repeated tile. */}
                                            {p.combined_emi != null
                                                && (o?.best_offer_emi == null
                                                    || Math.round(Number(p.combined_emi)) !== Math.round(Number(o.best_offer_emi)))
                                                && <Tile label="Combined EMI" value={formatLoanAmount(p.combined_emi)} color="purple" />}
                                            {p.risk_level && <Tile label="Risk" value={p.risk_level} color="red" />}
                                            {p.affordability_score != null && <Tile label="Affordability" value={p.affordability_score} color="green" />}
                                        </div>
                                    </div>
                                )}

                                {el && el.count > 0 && (
                                    <div className="mb-2">
                                        <div className="text-700 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Existing Loans</div>
                                        <div className="grid">
                                            <Tile label="Active Loans" value={el.count} color="primary" />
                                            <Tile label="Total Outstanding" value={formatLoanAmount(el.total_outstanding)} color="red" />
                                            <Tile label="Total EMI" value={formatLoanAmount(el.total_emi)} color="orange" />
                                        </div>
                                    </div>
                                )}

                                {(vKeys.length > 0 || b.has_cibil) && (
                                    <div>
                                        <div className="text-700 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Verification & Bureau</div>
                                        <div className="flex flex-wrap gap-1">
                                            {b.has_cibil && (
                                                <Tag
                                                    value={`CIBIL ${b.cibil_score}`}
                                                    icon="pi pi-chart-line"
                                                    style={{ ...outlineTagStyle('purple'), fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}
                                                />
                                            )}
                                            {vKeys.map((k) => (
                                                <Tag
                                                    key={k}
                                                    value={`${k.replace(/_/g, ' ')}${v[k].verified ? '' : ' (pending)'}`}
                                                    icon={v[k].verified ? 'pi pi-check' : 'pi pi-clock'}
                                                    style={{ ...outlineTagStyle(v[k].verified ? 'success' : 'secondary'), fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        );
                    })()}

                    {/* Quality Score Card */}
                    <Card className="shadow-1 mb-2">
                        <div className="flex align-items-center gap-1 mb-2">
                            <i className="pi pi-star text-primary" style={{ fontSize: '0.75rem' }} />
                            <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Lead Quality Assessment</h4>
                        </div>

                        <div className="flex flex-column md:flex-row gap-2 mb-2">
                            <div className="flex align-items-center gap-2">
                                <div className={`flex align-items-center justify-content-center bg-${getGradeColor(quality.grade)}-100 border-round`} style={{ width: '50px', height: '50px' }}>
                                    <span className={`text-${getGradeColor(quality.grade)}-600 font-bold`} style={{ fontSize: '1.5rem' }}>{quality.grade || 'E'}</span>
                                </div>
                                <div>
                                    <div className="text-900 font-bold mb-0" style={{ fontSize: '1rem' }}>{quality.score || 0}/100</div>
                                    <div className="text-500" style={{ fontSize: '0.65rem' }}>Quality Score</div>
                                </div>
                            </div>
                            <Divider layout="vertical" className="hidden md:block" />
                            <div className="flex-1">
                                <p className="text-600 m-0 line-height-3" style={{ fontSize: '0.7rem' }}>
                                    {quality.grade_explanation || getGradeDescription(quality.grade)}
                                </p>
                            </div>
                        </div>

                        <ProgressBar
                            value={quality.score || 0}
                            showValue={false}
                            className="mb-2"
                            style={{ height: '0.35rem' }}
                            color={`var(--${getGradeColor(quality.grade)}-500)`}
                         aria-label="Progress" />

                        {/* Quality Breakdown — product-aware presence+strength
                            (blueprint §9). Explains exactly WHY it's an A/B/C
                            per parameter: full (green) / partial (orange) /
                            absent (grey), with earned/weight + reason. Only
                            applicable params shown (PL-only params hidden for
                            non-PL — never penalised). */}
                        {quality.breakdown?.params && (
                            <>
                                <div className="flex justify-content-between align-items-center mb-1">
                                    <span className="text-600 font-medium" style={{ fontSize: '0.62rem' }}>Why this score</span>
                                    {quality.rubric_version && (
                                        <span className="text-400" style={{ fontSize: '0.55rem' }}>rubric {quality.rubric_version}</span>
                                    )}
                                </div>
                                <div className="grid">
                                    {Object.entries(quality.breakdown.params)
                                        .filter(([, p]) => p && p.applicable)
                                        .map(([key, p]) => {
                                            const labels = {
                                                cibil: 'CIBIL', eligibility: 'Eligibility', documents: 'Documents',
                                                verification: 'Verification', foir: 'FOIR', offers: 'Offers',
                                                bank: 'Bank', company: 'Employer', company_category: 'Company Category',
                                                interest: 'Interest', location: 'Location',
                                            };
                                            const full = p.earned >= p.weight;
                                            const partial = p.earned > 0 && !full;
                                            const c = full ? 'green' : (partial ? 'orange' : 'gray');
                                            const icon = full ? 'pi-check' : (partial ? 'pi-minus' : 'pi-times');
                                            return (
                                                <div key={key} className="col-12 md:col-6 p-1">
                                                    <div className="flex align-items-center gap-2 surface-50 border-round" style={{ padding: '0.4rem 0.5rem' }}>
                                                        <div className={`flex align-items-center justify-content-center border-circle bg-${c}-100`} style={{ width: '22px', height: '22px', flexShrink: 0 }}>
                                                            <i className={`pi ${icon} text-${c}-600`} style={{ fontSize: '0.6rem' }} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex justify-content-between align-items-center gap-2">
                                                                <span className="text-900 font-medium" style={{ fontSize: '0.68rem' }}>{labels[key] || key}</span>
                                                                <span className={`text-${c}-700 font-semibold white-space-nowrap`} style={{ fontSize: '0.62rem' }}>{p.earned}/{p.weight}</span>
                                                            </div>
                                                            <div className="text-500 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.58rem' }}>
                                                                {p.detail}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </>
                        )}
                    </Card>

                    {/* Documents — canonical schema-driven section (blueprint
                        §13.11), the same component the lead wizard /
                        collaboration view use. read-only pre-purchase:
                        groups → per-definition cards (uploaded vs required),
                        no upload controls, file access locked until purchase. */}
                    {(documents.count > 0 || docResolver.resolvedDocs?.length > 0) && (
                        <Card className="shadow-1 mb-2">
                            <div className="flex align-items-center justify-content-between mb-2">
                                <div className="flex align-items-center gap-1">
                                    <i className="pi pi-folder text-primary" style={{ fontSize: '0.75rem' }} />
                                    <h4 className="text-900 font-semibold m-0" style={{ fontSize: '0.8rem' }}>Documents</h4>
                                </div>
                                <Badge value={documents.count} severity="success" style={{ fontSize: '0.6rem' }} />
                            </div>
                            <MarketplaceDocumentsSection
                                resolvedDocs={docResolver.resolvedDocs}
                                groupedDocs={docResolver.groupedDocs}
                                loadingDocMapping={docResolver.loadingDocMapping}
                                uploadedDocs={docResolver.uploadedDocs}
                                uploadingDocs={docResolver.uploadingDocs}
                                uploadProgress={docResolver.uploadProgress}
                                getUploadedFilesForDefinition={docResolver.getUploadedFilesForDefinition}
                                handleDocumentSelect={docResolver.handleDocumentSelect}
                                handleDocumentRemove={docResolver.handleDocumentRemove}
                                canUpload={false}
                                isOwner={false}
                                readOnly
                            />
                            <div className="bg-yellow-50 border-round mt-2" style={{ padding: '0.4rem 0.5rem' }}>
                                <p className="text-yellow-700 m-0" style={{ fontSize: '0.65rem' }}>
                                    <i className="pi pi-lock mr-1" />
                                    Document access will be granted after purchase completion
                                </p>
                            </div>
                        </Card>
                    )}
                </div>
            </div>

            {/* Purchase Confirmation Dialog */}
            <Dialog
                visible={showPurchaseDialog}
                onHide={() => setShowPurchaseDialog(false)}
                header={
                    <div className="flex align-items-center gap-1">
                        <i className="pi pi-shopping-cart text-primary" style={{ fontSize: '0.85rem' }} />
                        <span style={{ fontSize: '0.9rem' }}>Confirm Purchase</span>
                    </div>
                }
                style={{ width: '400px' }}
                footer={
                    <div className="flex justify-content-between align-items-center">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text p-button-sm"
                            onClick={() => setShowPurchaseDialog(false)}
                            style={{ fontSize: '0.75rem' }}
                        />
                        <Button
                            label="Confirm Purchase"
                            icon="pi pi-check"
                            className="p-button-sm"
                            onClick={handlePurchase}
                            loading={purchasing}
                            style={{ fontSize: '0.75rem' }}
                        />
                    </div>
                }
            >
                <div className="surface-50 border-round mb-2" style={{ padding: '0.6rem' }}>
                    <div className="flex justify-content-between mb-2">
                        <span className="text-600" style={{ fontSize: '0.7rem' }}>Lead Type</span>
                        <Tag value={productLabel} style={{ ...outlineTagStyle('info'), fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />
                    </div>
                    <div className="flex justify-content-between mb-2">
                        <span className="text-600" style={{ fontSize: '0.7rem' }}>Loan Amount</span>
                        <span className="text-900 font-bold" style={{ fontSize: '0.75rem' }}>{formatLoanAmount(lead_details.loan_amount_required)}</span>
                    </div>
                    <div className="flex justify-content-between mb-2">
                        <span className="text-600" style={{ fontSize: '0.7rem' }}>Lead Commission</span>
                        <span className="text-primary font-bold" style={{ fontSize: '0.75rem' }}>{parseFloat((Number(collaboration.commission_percentage) || 3.0).toFixed(3))}%</span>
                    </div>
                    <Divider className="my-2" />
                    <div className="flex justify-content-between align-items-center">
                        <span className="text-600" style={{ fontSize: '0.7rem' }}>Quality Grade</span>
                        <LeadGradeBadge grade={quality.grade} size="small" />
                    </div>
                </div>

                <div className="field mb-2">
                    <label className="block text-600 font-medium mb-1" style={{ fontSize: '0.7rem' }}>Notes (Optional)</label>
                    <InputTextarea
                        value={purchaseNotes}
                        onChange={(e) => setPurchaseNotes(e.target.value)}
                        rows={2}
                        className="w-full"
                        placeholder="Add any notes about this purchase..."
                        style={{ fontSize: '0.75rem' }}
                    />
                </div>

                <div className="bg-blue-50 border-round" style={{ padding: '0.4rem 0.5rem' }}>
                    <p className="text-blue-700 m-0 line-height-3" style={{ fontSize: '0.65rem' }}>
                        <i className="pi pi-info-circle mr-1" />
                        Upon purchase, you'll receive full access to borrower details, documents, and direct contact information.
                    </p>
                </div>
            </Dialog>

            {/* Express Interest Dialog */}
            <ExpressInterestDialog
                visible={showInterestDialog}
                onHide={() => setShowInterestDialog(false)}
                lead={{
                    id: id,
                    borrower_name: borrower.full_name || borrower.name,
                    loan_amount: lead_details.loan_amount_required,
                    product_type: lead_details.product_type,
                    quality_grade: quality.grade,
                    commission_percentage: collaboration.commission_percentage,
                    seller_name: seller.full_name || seller.display_name
                }}
                suggestedPercentage={collaboration.commission_percentage}
                onSuccess={handleInterestSuccess}
            />

            {/* Credit Appraisal Memo — the SAME full-screen viewer the lead
                view uses. Opened from the CAM section button. Data masked on
                the fly by the backend; `lead` has no id (display-only). */}
            {cam && (
                <CAMPaperView
                    visible={showCamDialog}
                    onHide={() => setShowCamDialog(false)}
                    formData={cam.form_data || {}}
                    lead={{ lead_id_formatted: tracking_code, status, created_at: posted_at }}
                    selectedProduct={{ product_name: cam.product_name, display_name: cam.product_name }}
                    coApplicants={cam.co_applicants || []}
                    ocrPayslipDetails={cam.ocr_payslip || null}
                    individualPayslips={[]}
                    offers={(cam.offers || []).map(mapOfferForWidget)}
                    selectedOffer={mapOfferForWidget(cam.selected_offer)}
                    eligibilityResult={cam.eligibility || null}
                    eligibilityParams={null}
                />
            )}

            {/* Mobile Sticky Action Bar - Different states */}
            <div className="mobile-action-bar">
                {is_owner ? (
                    /* Owner's listing */
                    <div className="mobile-action-content mobile-owner">
                        <div className="mobile-status-info">
                            <i className="pi pi-info-circle text-blue-500" style={{ fontSize: '0.9rem' }} />
                            <span className="text-700 font-medium" style={{ fontSize: '0.7rem' }}>Your Listing</span>
                        </div>
                        <Button
                            label="Manage"
                            icon="pi pi-cog"
                            className="p-button-sm"
                            onClick={() => navigate('/app/marketplace/my-listings')}
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                        />
                    </div>
                ) : has_purchased ? (
                    /* Already purchased */
                    <div className="mobile-action-content mobile-purchased">
                        <div className="mobile-status-info">
                            <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.9rem' }} />
                            <span className="text-green-700 font-medium" style={{ fontSize: '0.7rem' }}>Purchased</span>
                        </div>
                        <Button
                            label="View Purchase"
                            icon="pi pi-external-link"
                            className="p-button-sm p-button-success"
                            onClick={() => navigate('/app/marketplace/my-purchases')}
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                        />
                    </div>
                ) : is_member ? (
                    /* Already collaborating */
                    <div className="mobile-action-content mobile-collaborating">
                        <div className="mobile-status-info">
                            <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.9rem' }} />
                            <div className="flex flex-column">
                                <span className="text-green-700 font-semibold" style={{ fontSize: '0.7rem' }}>Collaborating</span>
                                <span className="text-500" style={{ fontSize: '0.55rem' }}>You're working on this lead</span>
                            </div>
                        </div>
                        <Button
                            label="View Details"
                            icon="pi pi-arrow-right"
                            className="p-button-sm p-button-success"
                            onClick={() => navigate(`/app/collaborations/${my_collaboration_id}`)}
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                        />
                    </div>
                ) : has_expressed_interest ? (
                    /* Interest already expressed - pending */
                    <div className="mobile-action-content mobile-pending">
                        <div className="mobile-status-info">
                            <i className="pi pi-clock text-blue-500" style={{ animation: 'pulse 2s infinite', fontSize: '0.9rem' }} />
                            <div className="flex flex-column">
                                <span className="text-blue-700 font-semibold" style={{ fontSize: '0.7rem' }}>Enquiry Sent</span>
                                <span className="text-500" style={{ fontSize: '0.55rem' }}>Waiting for response</span>
                            </div>
                        </div>
                        <Button
                            label="My Enquiries"
                            icon="pi pi-arrow-right"
                            className="p-button-sm p-button-outlined"
                            onClick={() => navigate('/app/marketplace/my-interests')}
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                        />
                    </div>
                ) : (
                    /* Default - Can express interest */
                    <div className="mobile-action-content">
                        <div className="mobile-price-info">
                            <span className="mobile-fee-label" style={{ fontSize: '0.55rem' }}>Lead Commission</span>
                            <span className="mobile-fee-value" style={{ fontSize: '1rem' }}>{parseFloat((Number(collaboration.commission_percentage) || 3.0).toFixed(2))}%</span>
                        </div>
                        <div className="mobile-buttons">
                            <Button
                                label="Send Enquiry"
                                icon="pi pi-handshake"
                                className="p-button-sm mobile-interest-btn"
                                onClick={() => setShowInterestDialog(true)}
                                disabled={is_expired || !collaboration.is_open}
                                style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Responsive Styles */}
            <style>{`
                /* Compact Listing Detail Page */
                .listing-detail {
                    font-size: 0.875rem;
                }
                .listing-detail .p-card-body {
                    padding: 0.75rem;
                }
                .listing-detail h2 {
                    font-size: 1.1rem;
                }
                .listing-detail h4 {
                    font-size: 0.85rem;
                }
                .listing-detail h5 {
                    font-size: 0.8rem;
                }

                /* Quick Stats */
                .quick-stats-grid .stat-card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .quick-stats-grid .stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                .stat-icon {
                    font-size: 1.1rem;
                }
                .stat-value {
                    font-size: 0.95rem;
                }
                .stat-label {
                    font-size: 0.65rem;
                }

                /* Animations */
                @keyframes expiry-pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.6; }
                }
                .expiry-pulse {
                    animation: expiry-pulse 1.5s ease-in-out infinite;
                }
                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                }

                /* Mobile Action Bar - Hidden on Desktop */
                .mobile-action-bar {
                    display: none;
                }

                /* Mobile Responsive */
                @media (max-width: 991px) {
                    /* Show mobile action bar */
                    .mobile-action-bar {
                        display: block;
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        background: white;
                        border-top: 1px solid #E2E8F0;
                        padding: 0.75rem 1rem;
                        z-index: 1000;
                        box-shadow: 0 -4px 12px rgba(0,0,0,0.1);
                    }
                    .mobile-action-content {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        max-width: 100%;
                        gap: 1rem;
                    }
                    .mobile-price-info {
                        display: flex;
                        flex-direction: column;
                    }
                    .mobile-fee-label {
                        font-size: 0.7rem;
                        color: #64748B;
                        text-transform: uppercase;
                        letter-spacing: 0.02em;
                    }
                    .mobile-fee-value {
                        font-size: 1.25rem;
                        font-weight: 700;
                        color: #3B82F6;
                    }
                    .mobile-buttons {
                        display: flex;
                        gap: 0.5rem;
                    }
                    .mobile-interest-btn {
                        background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%) !important;
                        border: none !important;
                        font-weight: 600 !important;
                        white-space: nowrap;
                    }

                    /* Status info styles */
                    .mobile-status-info {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                    }
                    .mobile-status-info i {
                        font-size: 1.25rem;
                    }

                    /* Collaborating state */
                    .mobile-collaborating {
                        background: linear-gradient(90deg, var(--green-50) 0%, white 100%);
                    }

                    /* Pending interest state */
                    .mobile-pending {
                        background: linear-gradient(90deg, var(--blue-50) 0%, white 100%);
                    }

                    /* Owner state */
                    .mobile-owner {
                        background: linear-gradient(90deg, var(--surface-100) 0%, white 100%);
                    }

                    /* Purchased state */
                    .mobile-purchased {
                        background: linear-gradient(90deg, var(--green-50) 0%, white 100%);
                    }

                    /* Add padding at bottom to account for sticky bar */
                    .listing-detail {
                        padding-bottom: 80px;
                    }

                    /* Hide desktop sidebar action card on mobile - make it non-sticky */
                    .col-12.lg\\:col-4 .shadow-1.sticky {
                        position: relative !important;
                        top: 0 !important;
                    }

                    /* Breadcrumb header compact */
                    .listing-detail > .surface-card.border-round.shadow-1.p-3.mb-4 {
                        padding: 0.625rem 0.75rem !important;
                        margin-bottom: 0.75rem !important;
                    }

                    /* Cards reduce margins */
                    .listing-detail .p-card {
                        margin-bottom: 0.75rem !important;
                    }
                }

                @media (max-width: 576px) {
                    /* Compact stats on mobile */
                    .stat-icon {
                        font-size: 0.9rem;
                    }
                    .stat-value {
                        font-size: 0.8rem;
                    }
                    .stat-label {
                        font-size: 0.6rem;
                    }
                    .quick-stats-grid .stat-card {
                        padding: 0.5rem !important;
                    }

                    /* Mobile action bar adjustments */
                    .mobile-action-bar {
                        padding: 0.5rem 0.6rem;
                    }
                    .mobile-fee-value {
                        font-size: 0.9rem;
                    }
                    .mobile-interest-btn {
                        font-size: 0.65rem !important;
                        padding: 0.35rem 0.5rem !important;
                    }
                    .mobile-interest-btn .pi {
                        font-size: 0.65rem;
                    }
                    .mobile-status-info i {
                        font-size: 0.85rem;
                    }
                    .mobile-status-info .text-sm {
                        font-size: 0.65rem !important;
                    }
                    .mobile-status-info .text-xs {
                        font-size: 0.55rem !important;
                    }

                    /* Breadcrumb compact */
                    .listing-detail > .surface-card.border-round.shadow-1 {
                        padding: 0.35rem 0.5rem !important;
                    }
                    .listing-detail .surface-card .text-500,
                    .listing-detail .surface-card .text-900.font-medium {
                        font-size: 0.65rem;
                    }
                    /* Hide breadcrumb middle text on very small screens */
                    .listing-detail > .surface-card .pi-angle-right,
                    .listing-detail > .surface-card > .flex > .flex > .text-500:not(:first-child) {
                        display: none;
                    }

                    /* Tracking code in header */
                    .listing-detail > .surface-card .font-mono {
                        font-size: 0.6rem !important;
                    }

                    /* Header adjustments */
                    .listing-detail h2 {
                        font-size: 0.9rem !important;
                    }
                    .listing-detail h4 {
                        font-size: 0.75rem !important;
                    }

                    /* Card padding adjustments */
                    .listing-detail .p-card-body {
                        padding: 0.6rem;
                    }

                    /* Lead details grid */
                    .listing-detail .col-6.md\\:col-4 {
                        padding: 0.15rem;
                    }
                    .listing-detail .col-6.md\\:col-4 .surface-50 {
                        padding: 0.35rem !important;
                    }
                    .listing-detail .col-6.md\\:col-4 .text-sm {
                        font-size: 0.55rem;
                    }
                    .listing-detail .col-6.md\\:col-4 .font-semibold {
                        font-size: 0.65rem;
                    }

                    /* Borrower info compact */
                    .listing-detail .surface-50.border-round.p-4 {
                        padding: 0.4rem !important;
                    }
                    .listing-detail .surface-50 .text-xs {
                        font-size: 0.5rem;
                    }
                    .listing-detail .surface-50 .font-medium {
                        font-size: 0.65rem;
                    }
                    /* Borrower info icons smaller */
                    .listing-detail .surface-50 .border-circle {
                        width: 20px !important;
                        height: 20px !important;
                    }
                    .listing-detail .surface-50 .border-circle i {
                        font-size: 0.6rem;
                    }

                    /* Quality score section */
                    .listing-detail .bg-green-100,
                    .listing-detail .bg-blue-100,
                    .listing-detail .bg-orange-100 {
                        width: 40px !important;
                        height: 40px !important;
                    }
                    .listing-detail .text-4xl {
                        font-size: 1.25rem !important;
                    }
                    .listing-detail .text-2xl.mb-1 {
                        font-size: 0.95rem !important;
                    }

                    /* Sidebar cards compact */
                    .listing-detail .col-12.lg\\:col-4 .text-5xl {
                        font-size: 1.5rem !important;
                    }
                    .listing-detail .col-12.lg\\:col-4 .p-avatar-xl {
                        width: 2.25rem !important;
                        height: 2.25rem !important;
                    }

                    /* Collaboration terms compact */
                    .listing-detail .col-12.lg\\:col-4 .p-4.bg-primary-50 {
                        padding: 0.5rem !important;
                    }

                    /* Tags smaller */
                    .listing-detail .p-tag {
                        font-size: 0.55rem !important;
                        padding: 0.1rem 0.25rem !important;
                    }

                    /* Grade badge smaller */
                    .listing-detail .lead-grade-badge-large {
                        transform: scale(0.75);
                    }
                }

                /* Tablet adjustments */
                @media (min-width: 577px) and (max-width: 991px) {
                    .mobile-interest-btn {
                        padding: 0.4rem 0.8rem !important;
                    }
                    .mobile-status-info {
                        gap: 0.5rem;
                    }
                    .listing-detail .p-card-body {
                        padding: 0.65rem;
                    }
                    .stat-icon {
                        font-size: 1rem;
                    }
                    .stat-value {
                        font-size: 0.85rem;
                    }
                    .stat-label {
                        font-size: 0.6rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default ListingDetail;
